import { ReactNode, useState } from 'react';
import { map } from 'lodash';
import {
  CheckboxVisibility,
  ShimmeredDetailsList,
  SelectionMode,
  CommandBar,
  DetailsListLayoutMode,
  CollapseAllVisibility,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { hydrateStandardIColumnList } from 'util/ui/detailList/hydrateStandardIColumnList';
import { CollapsedPane } from 'component/CollapsedPane/CollapsedPane';

/** Takes in a CVSS score as a vector string and renders a
 * Table of the components and their meanings.
 *
 * @example
 * <CvssTable vectorString='CVSS:3.1/AV:N/AC:H/PR:H/UI:R/S:U/C:N/I:N/A:N/E:U/RL:T/RC:R'/>
 */
export const CvssTable = ({
  children,
  vectorString = null,
  loading = false,
}: ICvssTableProps) => {
  const [showAll, setShowAll] = useState<boolean | null>(false);
  const [t] = useTranslation(['cvss', 'search', 'vulnerability']);
  const componentList = vectorString?.toUpperCase()?.split('/');
  if (!loading && (!componentList || componentList.length < 2)) {
    return null;
  }

  //dont display css score since we do above it
  if (componentList?.[0]?.includes('CVSS')) {
    componentList.shift();
  }
  const makeCvssItem = ({
    metric: shortMetric,
    value: shortValue,
  }: {
    metric: string;
    value: string;
  }): {
    metric: string;
    value: string;
    shortCode: string;
    shortMetric: string;
  } => {
    const shortCode = shortMetric + ':' + shortValue;
    //note: all this -thing- stuff is here because the default separators are
    // : for ns and . or : for objects
    const value = t('cvss-ns-metric-obj-' + shortCode, {
      nsSeparator: '-ns-',
      keySeparator: '-obj-',
    });
    const metric = t('cvss-ns-metric-obj-' + shortMetric, {
      nsSeparator: '-ns-',
      keySeparator: '-obj-',
    });

    return { metric, value, shortCode, shortMetric };
  };

  // the metrics list in short-codes
  const shortList = componentList?.map(e => {
    const [metric, value] = e.split(':');
    return { metric, value };
  });
  //the metrics list in full text
  const itemList = map(shortList, makeCvssItem);
  //inject translations on groupnames
  const g = makeGroupList(shortList)?.map(g => {
    g.name = t(g.name, { ns: ['search', 'cvss'] });
    return g;
  });

  const onRenderItemColumn: (
    item?: any,
    index?: number,
    column?: import('@fluentui/react').IColumn
  ) => ReactNode = (item, index, column) => {
    if (!column || !item) {
      return null;
    }
    const data = item?.[column.key];
    switch (column.key) {
      case 'metric': {
        const code = item?.shortMetric.toUpperCase();
        const tooltip = t('description-obj-' + code, {
          nsSeparator: '-ns-',
          keySeparator: '-obj-',
          defaultValue: null,
        });
        if (tooltip) {
          return (
            <CollapsedPane
              label={t(data, data, { ns: 'cvss' })}
              showAll={showAll}
              setShowAll={setShowAll}
            >
              {tooltip}
            </CollapsedPane>
          );
        }
        return data;
      }
      case 'value': {
        const code = item?.shortCode?.toUpperCase();
        const tooltip = t('description-obj-' + code, {
          nsSeparator: '-ns-',
          keySeparator: '-obj-',
          defaultValue: null,
        });
        if (tooltip) {
          return (
            <CollapsedPane
              label={t(data, data, { ns: 'cvss' })}
              showAll={showAll}
              setShowAll={setShowAll}
            >
              {tooltip}
            </CollapsedPane>
          );
        }
        return data;
      }
      default:
        return data;
    }
  };
  const columns = hydrateStandardIColumnList(
    [
      {
        key: 'metric',
        minWidth: 200,
        isResizable: true,
        targetWidthProportion: 1,
      },
      {
        key: 'value',
        minWidth: 200,
        isResizable: true,
        targetWidthProportion: 1,
      },
    ],
    t
  );
  const tableName = t('vulnerability:cvss:CVSS Score', {
    returnObjects: true,
    defaultValue: 'CVSS Scores',
  }) as string;

  return (
    <div id='cvss-table'>
      {children}
      <CommandBar
        items={[]}
        farItems={[
          {
            key: 'showAll',
            text: t('Expand all', {
              ns: 'translation',
              defaultValue: 'Expand all',
            }),
            title: t('Expand all', {
              ns: 'translation',
              defaultValue: 'Expand all',
            }),
            ariaLabel: t('vulnerability:cvss:cvssTableExpandAll', {
              ns: 'translation',
              defaultValue: 'Expand All CVSS Score table rows',
            }),
            iconProps: { iconName: 'ChevronDownSmall' },
            onClick: () => setShowAll(true),
            disabled: showAll === null ? false : showAll,
          },
          {
            key: 'hideAll',
            text: t('Collapse all', {
              ns: 'translation',
              defaultValue: 'Collapse all',
            }),
            title: t('Collapse all', {
              ns: 'translation',
              defaultValue: 'Collapse all',
            }),
            ariaLabel: t('vulnerability:cvss:cvssTableCollapseAll', {
              ns: 'translation',
              defaultValue: 'Collapse All CVSS Score table rows',
            }),
            iconProps: { iconName: 'ChevronRightSmall' },
            onClick: () => setShowAll(false),
            disabled: showAll === null ? false : !showAll,
          },
        ]}
      />
      <ShimmeredDetailsList
        ariaLabel={tableName}
        ariaLabelForGrid={tableName}
        listProps={{
          title: tableName,
        }}
        cellStyleProps={{
          cellExtraRightPadding: 4,
          cellLeftPadding: 4,
          cellRightPadding: 4,
        }}
        groupProps={{ collapseAllVisibility: CollapseAllVisibility.hidden }}
        compact={true}
        checkboxVisibility={CheckboxVisibility.hidden}
        columns={columns}
        groups={g}
        items={itemList}
        shimmerLines={6}
        enableShimmer={loading}
        layoutMode={DetailsListLayoutMode.justified}
        selectionMode={SelectionMode.none}
        onRenderItemColumn={onRenderItemColumn}
      />
    </div>
  );
};

const makeGroupList = (
  metricList?: { metric: string; value: string }[]
): import('@fluentui/react').IGroup[] | undefined => {
  if (!metricList) {
    return;
  }
  const offsetFrom = (keyList: string[]) => {
    for (const k in keyList) {
      const i = metricList?.findIndex(m => m.metric === keyList[k]);
      if (i >= 0) {
        return i;
      }
    }
    return metricList?.length;
  };

  const offsetList = {
    base: 0,
    temporal: offsetFrom(['E', 'RL', 'RC']),
    end: metricList?.length,
  };

  const groups: import('@fluentui/react').IGroup[] = [
    {
      key: 'base',
      name: 'Base score metrics',
      startIndex: offsetList.base,
      count: offsetList.temporal,
    },
    {
      key: 'temporal',
      name: 'Temporal score metrics',
      startIndex: offsetList.temporal,
      count: offsetList.end - offsetList.temporal,
    },
  ].filter(e => e.count > 0);

  return groups;
};

export interface ICvssTableProps {
  vectorString?: string | null;
  loading?: boolean;
  children?: ReactNode;
}
